import React from 'react';
import PropTypes from 'prop-types';
import { Paper, Stack, Box, Typography, CircularProgress } from '@mui/material';
import dayjs from 'dayjs';
import { useIncomeTotalByMonth } from './hooks/useIncomeTotalByMonth';
import { useExpenseTotalByMonth } from './hooks/useExpenseTotalByMonth';

export default function FinancialResult({ selectedDate, compact }) {
  const {
    total: incomeTotal,
    totalFormatted: incomeTotalFormatted,
    isLoading: isIncomeLoading,
  } = useIncomeTotalByMonth(selectedDate);

  const {
    total: expenseTotal,
    totalFormatted: expenseTotalFormatted,
    isLoading: isExpenseLoading,
  } = useExpenseTotalByMonth(selectedDate);

  const isLoading = isIncomeLoading || isExpenseLoading;

  const result = Number(incomeTotal) - Number(expenseTotal);
  const resultFormatted = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(result);

  let resultColor = 'text.primary';
  if (result > 0) {
    resultColor = 'success.main';
  } else if (result < 0) {
    resultColor = 'error.main';
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        px: 2,
        py: 1.5,
        borderRadius: 2,
        width: compact ? '100%' : 'fit-content',
      }}
    >
      <Stack
        direction="row"
        spacing={compact ? 1 : 2}
        alignItems="center"
        justifyContent={compact ? 'space-between' : 'flex-start'}
      >
        <Box>
          <Typography
            color="text.secondary"
            display="block"
            sx={{ fontSize: '0.65rem', whiteSpace: 'nowrap' }}
          >
            Total de Entradas
          </Typography>
          <Typography
            variant={compact ? 'body2' : 'subtitle1'}
            color="success.main"
            fontWeight="bold"
          >
            {incomeTotalFormatted}
          </Typography>
        </Box>

        {!compact && (
          <Typography variant="h6" color="text.secondary">
            &minus;
          </Typography>
        )}

        <Box>
          <Typography
            color="text.secondary"
            display="block"
            sx={{ fontSize: '0.65rem', whiteSpace: 'nowrap' }}
          >
            Total de Saídas
          </Typography>
          <Typography
            variant={compact ? 'body2' : 'subtitle1'}
            color="error.main"
            fontWeight="bold"
          >
            {expenseTotalFormatted}
          </Typography>
        </Box>

        {!compact && (
          <Typography variant="h6" color="text.secondary">
            =
          </Typography>
        )}

        <Box>
          <Typography
            color="text.secondary"
            display="block"
            sx={{ fontSize: '0.65rem', whiteSpace: 'nowrap' }}
          >
            Resultado
          </Typography>
          {isLoading ? (
            <CircularProgress size={18} />
          ) : (
            <Typography
              variant={compact ? 'body2' : 'subtitle1'}
              color={resultColor}
              fontWeight="bold"
            >
              {resultFormatted}
            </Typography>
          )}
        </Box>
      </Stack>
    </Paper>
  );
}

FinancialResult.propTypes = {
  selectedDate: PropTypes.object,
  compact: PropTypes.bool,
};

FinancialResult.defaultProps = {
  selectedDate: dayjs(),
  compact: false,
};
